import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { IPasswordChangeRequest, IUserPreferences } from './models';
import { AccountUser, IUser } from './models/user';
import { USERS_API_TOKEN } from './tokens';

@Injectable()
export class UsersApiService {
  constructor(
    private http: HttpClient,
    @Inject(USERS_API_TOKEN) private url: string,
  ) {
    if (!url) throw new Error('USERS_API token not provided');
  }

  public getCurrent() {
    return this.http.get<IUser>(`${this.url}/current`);
  }

  public postCurrent(user: IUser) {
    return this.http.post<IUser>(`${this.url}/current`, user);
  }

  public logout() {
    return this.http.post(`${this.url}/logout`, {});
  }

  public changePassword(passwordChangeRequest: IPasswordChangeRequest) {
    return this.http.post(`${this.url}/password`, passwordChangeRequest, {
      headers: {
        'epc-protected': passwordChangeRequest.passwordToken ? 'false' : 'true',
      },
    });
  }

  public postLanguage(languageCode: string) {
    return this.http.post<void>(`${this.url}/language`, { languageCode });
  }

  public postPreferences(preferences: IUserPreferences) {
    return this.http.post<IUser>(`${this.url}/preferences`, preferences);
  }

  public setIsSubscribedToAccountBaskets(value: boolean) {
    return this.http.put<void>(
      `${this.url}/current/isSubscribedToAccountBaskets`,
      value,
    );
  }

  public getActiveBasketId(accountId: string) {
    return this.http.get<string>(
      `${this.url}/preferences/account/${accountId}/basketId`,
    );
  }

  public setActiveBasketId(accountId: string, basketId: string | null) {
    return this.http.post(
      `${this.url}/preferences/account/${accountId}/basketId?key=`,
      `"${basketId}"`,
      {
        headers: { 'Content-Type': 'application/json' },
      },
    );
  }

  public getAccountUsers(accountId: string) {
    return this.getAccountsUsers([accountId]);
  }

  public getAccountsUsers(accountsIds: string[]) {
    const accountsIdsFilter = `[["accountId","=",["${accountsIds.join(
      '","',
    )}"]]]`;
    const params = new HttpParams()
      .set(`page`, `1`)
      .set(`pageSize`, `200`)
      .set(`filter`, accountsIdsFilter)
      .set(`group`, `[{"field":"account.Id"}]`);
    return this.http
      .get(`/api/v1/users/useraccounts`, { params })
      .pipe(map(mapToAccountUsers));
  }

  public getUsersByExternalId(externalIds: string[]): Observable<IUser[]> {
    if (!externalIds || externalIds.length === 0) return of([]);
    return this.http.post<IUser[]>(`${this.url}/getByFilter`, {
      externalIds,
    });
  }
}

function mapToAccountUsers(res: {
  groups: [{ items: [{ user: AccountUser }] }];
}): AccountUser[] {
  return res?.groups?.[0]?.items.map((item) => item?.user) ?? [];
}
